import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, Plus, X } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateOrganization } from '../hooks/use-create-organization';

type FormValues = { name: string };

interface CreateOrganizationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function CreateOrganizationDialog({ open, onOpenChange }: CreateOrganizationDialogProps) {
    const { t } = useTranslation();
    const createOrg = useCreateOrganization();

    const schema = useMemo(
        () =>
            z.object({
                name: z.string().min(1, t('organization.validation.nameRequiredOnCreate')).max(255),
            }),
        [t],
    );

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormValues>({ resolver: zodResolver(schema) });

    const onSubmit = (values: FormValues) => {
        createOrg.mutate(
            { name: values.name },
            {
                onSuccess: () => {
                    reset();
                    onOpenChange(false);
                },
            },
        );
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) reset();
        onOpenChange(open);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader className="pb-2">
                    <DialogTitle className="flex items-center gap-3">
                        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                            <Building2 className="size-5" />
                        </div>
                        <span>{t('organization.create.title')}</span>
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-2 px-6 py-5">
                        <Label htmlFor="org-name">{t('organization.create.nameLabel')}</Label>
                        <Input
                            id="org-name"
                            placeholder={t('organization.create.namePlaceholder')}
                            autoFocus
                            className="h-10"
                            {...register('name')}
                        />
                        {errors.name && (
                            <p className="text-sm text-error-600">{errors.name.message}</p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleOpenChange(false)}
                            disabled={createOrg.isPending}
                        >
                            <X className="size-4" />
                            {t('organization.actions.cancel')}
                        </Button>
                        <Button type="submit" disabled={createOrg.isPending}>
                            <Plus className="size-4" />
                            {createOrg.isPending
                                ? t('organization.create.submitting')
                                : t('organization.create.submit')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
